import { getScopedReturnMediaIds } from "./returnMedia";

export const isAgentDraftItem = (item) => item?.return_type === "connect_to_agent";

export function buildAgentDraftPayload(order, items, contact, uploads) {
  return {
    order_id: String(order.order_id),
    line_items: items,
    uploaded_media_ids: Array.from(new Set([
      ...getScopedReturnMediaIds(uploads, items),
      ...items.flatMap((item) => item.uploaded_media_ids || []),
    ])),
    ...contact,
  };
}

const attemptKey = (merchantId, orderId) => `agent_draft_attempt:${merchantId}:${orderId}`;

export function readAgentDraftAttempt(merchantId, orderId) {
  try {
    return JSON.parse(sessionStorage.getItem(attemptKey(merchantId, orderId)) || "null");
  } catch (_) {
    return null;
  }
}

export function prepareAgentDraftAttempt(merchantId, payload) {
  const previous = readAgentDraftAttempt(merchantId, payload.order_id);
  if (previous) {
    const payloads = previous.payloads || [previous.payload];
    const history = payloads.filter((item) => JSON.stringify(item) !== JSON.stringify(payload));
    const attempt = { ...previous, payload, payloads: [...history, payload] };
    // Preserve every distinct body before sending: any earlier in-flight body may win this key.
    sessionStorage.setItem(attemptKey(merchantId, payload.order_id), JSON.stringify(attempt));
    return attempt;
  }
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0"));
  const idempotencyKey = [hex.slice(0, 4), hex.slice(4, 6), hex.slice(6, 8), hex.slice(8, 10), hex.slice(10)].map((part) => part.join("")).join("-");
  const attempt = { payload, idempotencyKey, payloads: [payload] };
  // Persist before sending so a reload after an uncertain response reuses the same key.
  sessionStorage.setItem(attemptKey(merchantId, payload.order_id), JSON.stringify(attempt));
  return attempt;
}

export function clearAgentDraftAttempt(merchantId, orderId) {
  try { sessionStorage.removeItem(attemptKey(merchantId, orderId)); } catch (_) {}
}

export function validateAgentDraftResponse(data) {
  if (!data?.draft_id || data.status !== "draft" || !String(data.reference_id || "").startsWith("AGENT-")) {
    throw new Error("The draft could not be confirmed. Please retry; your selections are retained.");
  }
  return data;
}

const receiptKey = (merchantId, orderId) => `agent_draft_receipt:${merchantId}:${orderId}`;

export function readAgentDraftReceipt(merchantId, orderId, remainingItems) {
  try {
    const receipt = JSON.parse(sessionStorage.getItem(receiptKey(merchantId, orderId)) || "null");
    if (!receipt || receipt.remainingItems !== JSON.stringify(remainingItems)) return null;
    validateAgentDraftResponse(receipt);
    return receipt;
  } catch (_) {
    return null;
  }
}

export function saveAgentDraftReceipt(merchantId, orderId, draft, remainingItems) {
  const stored = readMixedAgentDraftReceipt(merchantId, orderId);
  const previous = stored?.draft_id === draft.draft_id ? stored : {};
  const receipt = { ...previous, ...draft, persisted: true, hasRemaining: remainingItems.length > 0, hasAgentRemaining: remainingItems.some(isAgentDraftItem), remainingItems: JSON.stringify(remainingItems) };
  if (previous.mixed || draft.mixed) {
    receipt.mixed = true;
    receipt.operational_unique_ids = Array.from(new Set([
      ...(Array.isArray(previous.operational_unique_ids) ? previous.operational_unique_ids : []),
      ...(Array.isArray(draft.operational_unique_ids) ? draft.operational_unique_ids : []),
    ]));
    receipt.payment_bindings = Array.from(new Map([
      ...(Array.isArray(previous.payment_bindings) ? previous.payment_bindings : []),
      ...(Array.isArray(draft.payment_bindings) ? draft.payment_bindings : []),
    ].map((binding) => [JSON.stringify(binding), binding])).values());
  }
  try {
    sessionStorage.setItem(receiptKey(merchantId, orderId), JSON.stringify(receipt));
    return receipt;
  } catch (_) { return { ...receipt, persisted: false }; }
}

export function clearAgentDraftReceipt(merchantId, orderId) {
  try { sessionStorage.removeItem(receiptKey(merchantId, orderId)); } catch (_) {}
}

export function hasPersistedAgentCart(returnedProducts, uploadMediaIds) {
  try {
    const state = JSON.parse(sessionStorage.getItem("persist:root") || "null");
    return state &&
      JSON.stringify(JSON.parse(state.returnedReducer).returnedProducts) === JSON.stringify(returnedProducts) &&
      JSON.stringify(JSON.parse(state.productReducer).uploadMediaIds) === JSON.stringify(uploadMediaIds);
  } catch (_) { return false; }
}

export function readMixedAgentDraftReceipt(merchantId, orderId) {
  try {
    const receipt = JSON.parse(sessionStorage.getItem(receiptKey(merchantId, orderId)) || "null");
    if (receipt?.mixed !== true || receipt.persisted !== true || !Array.isArray(receipt.agent_items)) return null;
    validateAgentDraftResponse(receipt);
    return receipt;
  } catch (_) { return null; }
}

export function readAgentDraftReceiptForOperation(merchantId, orderId, uniqueId) {
  if (!uniqueId) return null;
  const receipt = readMixedAgentDraftReceipt(merchantId, orderId);
  return Array.isArray(receipt?.operational_unique_ids) && receipt.operational_unique_ids.includes(String(uniqueId)) ? receipt : null;
}

const paymentIdentity = (data = {}) => {
  const value = (key) => typeof data.get === "function" ? data.get(key) : data[key];
  return {
    reference_id: String(value("reference_id") || "").trim(),
    transaction_id: String(value("transaction_id") || value("txn_id") || "").trim(),
    order_id: String(value("order_id") || "").trim(),
  };
};

export function bindAgentDraftPayment(merchantId, orderId, operationId, paymentData) {
  const receipt = readAgentDraftReceiptForOperation(merchantId, orderId, operationId);
  const identity = paymentIdentity(paymentData);
  if (!receipt || (!identity.reference_id && !identity.transaction_id)) return false;
  const binding = { operation_id: String(operationId), ...identity };
  const bindings = Array.isArray(receipt.payment_bindings) ? receipt.payment_bindings : [];
  const collisions = bindings.filter((item) => item && (
    (binding.reference_id && item.reference_id === binding.reference_id) ||
    (binding.transaction_id && item.transaction_id === binding.transaction_id)));
  if (collisions.some((item) => item.operation_id !== binding.operation_id)) return false;
  if (bindings.some((item) => JSON.stringify(item) === JSON.stringify(binding))) return true;
  try {
    sessionStorage.setItem(receiptKey(merchantId, orderId), JSON.stringify({ ...receipt, payment_bindings: [...bindings, binding] }));
    return true;
  } catch (_) { return false; }
}

export function resolveAgentDraftPaymentOperation(merchantId, orderId, paymentData) {
  const receipt = readMixedAgentDraftReceipt(merchantId, orderId);
  const identity = paymentIdentity(paymentData);
  if (!receipt || (!identity.reference_id && !identity.transaction_id)) return null;
  const bindings = Array.isArray(receipt.payment_bindings) ? receipt.payment_bindings : [];
  const operations = new Set(bindings.filter((binding) => {
    if (!binding || typeof binding !== "object") return false;
    if (!Array.isArray(receipt.operational_unique_ids) || !receipt.operational_unique_ids.includes(binding.operation_id)) return false;
    if (identity.order_id && binding.order_id && identity.order_id !== binding.order_id) return false;
    return identity.reference_id ? binding.reference_id === identity.reference_id : binding.transaction_id === identity.transaction_id;
  }).map((binding) => binding.operation_id));
  return operations.size === 1 ? operations.values().next().value : null;
}
